import React from 'react';
import { AnalysisRemarkKeys } from '../../remarks/config';
import { AIRemarksContainer } from '../../remarks/components/aiRemarksContainer';
import { useInterviewFindings } from '../../../hooks/useCaseInterviews'
import { InlineLoadingSpinner } from '../../../components/ui/spinners/InlineLoadingSpinner';
import { ErrorDisplay } from '../../../components/ui/error/ErrorDisplay';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { fetchInterviewsStep } from '../../../thunks/AnalysisStep';
import { useCaseNotes } from '../../../hooks/useCaseNotes';
import { CaseNoteType } from '../../../api/CaseNote';
import InterviewFindingsEmptyState from './InterviewFindings/EmptyState';
import InterviewList from './InterviewFindings/InterviewList';
import InterviewFindingsForm from './InterviewFindings/InterviewForm';
import { OptionNameValuePair } from '../../../lib/interfaces/types';
import InterviewUnSavedChangesModal from './InterviewFindings/ChangesModal';
import { SaveButtonV2 } from '../../../components/ui/button/v2/SaveButton';
import ButtonV2 from '../../../components/ui/button/v2';
import { useInterviewFormManager } from './InterviewFindings/useInterviewFormManager';
import { getInvestigators } from '../../users/logic/slice';
import { RETRIEVING_DATA_SUBTITLE } from '../../../lib/constants';

const InterviewFindingsAccordionContent: React.FC = () => {
    const { id: caseId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();

    const {
        interviewAnalysisStatus,
        interviews,
        createInterviewStatus,
        interviewsData
    } = useInterviewFindings();

    const {
        selectedInterviewId,
        formPayload,
        hasUnsavedChanges,
        isBusy,
        modalState,
        error,
        requestSelection,
        initiateNewEntry,
        updateField,
        saveChanges,
        discardChanges,
        resolveConflict,
        clearError
    } = useInterviewFormManager({
        caseId: caseId || '',
        interviews,
        createInterviewStatus
    });

    const {
        initialValue,
        ...remarksProps
    } = useCaseNotes({
        noteType: CaseNoteType.interview_findings,
    });

    const investigators = useAppSelector(getInvestigators);
    const investigatorOptions: OptionNameValuePair<string>[] = investigators.map(investigator => ({ 
        name: investigator.user_name, 
        value: investigator.email_id 
    }));

    const onRetry = () => {
        if (caseId) {
            dispatch(fetchInterviewsStep({ case_id: caseId }));
        }
    };

    return (
        <div id="interview-findings">
            {interviewAnalysisStatus === 'loading' ?
                <InlineLoadingSpinner
                    message="Loading Interviews..."
                    subtitle={RETRIEVING_DATA_SUBTITLE}
                    className="min-h-[400px]"
                />
                :
                interviewAnalysisStatus === 'error' ?
                    <ErrorDisplay
                        error="Failed to load interview data. Please retry."
                        onRetry={onRetry}
                    />
                    :
                    interviews.length === 0 ?
                        <InterviewFindingsEmptyState onAddInterview={initiateNewEntry} />
                        :
                        <div className='flex flex-col gap-4'>
                            <div className='flex gap-4'>
                                <div className='basis-1/5 h-[inherit]'>
                                    <InterviewList
                                        interviews={interviews}
                                        selectedInterviewId={selectedInterviewId}
                                        onAddInterview={initiateNewEntry}
                                        onSelectInterview={isBusy ? () => {} : requestSelection}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <InterviewFindingsForm
                                        investigatorOptions={investigatorOptions}
                                        interviews={interviews}
                                        selectedInterviewId={selectedInterviewId}
                                        data={formPayload}
                                        onChange={updateField}
                                        disabled={isBusy}
                                    />
                                    {error && (
                                        <ErrorDisplay 
                                            error={error}
                                            onRetry={() => {
                                                clearError();
                                                saveChanges();
                                            }}            
                                        />
                                    )}
                                </div>
                            </div>
                            {(hasUnsavedChanges || selectedInterviewId?.startsWith('temp-')) && selectedInterviewId && (
                                <div className='bg-slate-100 flex flex-row-reverse gap-4 items-center p-2 rounded-md'>
                                    <SaveButtonV2
                                        disabled={isBusy}
                                        loading={isBusy}
                                        onClick={saveChanges}
                                    />
                                    {selectedInterviewId?.startsWith('temp-') && (
                                        <ButtonV2
                                            variant="ghost"
                                            size="md"
                                            disabled={isBusy}
                                            onClick={discardChanges}
                                        >
                                            Cancel
                                        </ButtonV2>
                                    )}
                                    <span className='text-sm text-muted-color'>
                                        {isBusy 
                                            ? 'Saving interview...'
                                            : selectedInterviewId?.startsWith('temp-') 
                                                ? 'Save the new interview or cancel to add another' 
                                                : 'You have made changes, save now'}
                                    </span>
                                </div>
                            )}
                            <hr />
                            <AIRemarksContainer
                                remarkKey={AnalysisRemarkKeys.InterviewFindings}
                                initialValue={initialValue}
                                {...remarksProps}
                            />
                        </div>
            }
            {modalState.isOpen && (
                <InterviewUnSavedChangesModal
                    onAction={resolveConflict}
                />
            )}
        </div>
    );
};

export default InterviewFindingsAccordionContent;
